import { colors } from '@frontend/constants/theme';
import { IconHandwrittenChevronRight } from '@frontend/web-icons';
import { Text, XStack, YStack } from '@frontend/web-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

type AnimatedStrikeThroughProps = {
  delay: number;
  duration: number;
  fullWidth: number;
  textWidth: number;
};

type LetterRecommendationTaskSuggestionProps = {
  baseDuration?: number;
  disabled?: boolean;
  onPress?: () => void;
  text: string;
};

const generateRandomWavyPath = (width: number): string => {
  const segments = Math.max(4, Math.floor(width / 20));
  const baseAmplitude = 1;
  const randomSeed = Math.random() * 10; // Random seed for variation

  const points = [...Array(segments + 1)].map((_, index) => {
    const x = (index / segments) * width;
    const progress = index / segments;

    // Vary the amplitude slightly along the path
    const amplitudeVariation = Math.sin(progress * Math.PI) * 0.5;
    const currentAmplitude = baseAmplitude + amplitudeVariation;

    // Use both sine and cosine with random seed for more natural variation
    const y =
      5 +
      Math.sin(((index + randomSeed) * Math.PI) / 2) * currentAmplitude +
      Math.cos(((index + randomSeed) * Math.PI) / 3) * currentAmplitude * 0.5;

    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });

  return `M${points.join(' L')}`;
};

const AnimatedStrikeThrough = React.memo(({ delay, duration, textWidth }: AnimatedStrikeThroughProps) => {
  const [animatedWidth, setAnimatedWidth] = useState(delay === 0 && duration === 0 ? textWidth : 0);
  const [animationComplete, setAnimationComplete] = useState(delay === 0 && duration === 0);
  const animationRef = useRef<number>(undefined);

  const path = useMemo(() => generateRandomWavyPath(textWidth), [textWidth]);

  useEffect(() => {
    if (animationComplete) return;

    // eslint-disable-next-line fp/no-let
    let startTime: number;

    const animate = (timestamp: number) => {
      // eslint-disable-next-line fp/no-mutation
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;

      if (elapsed < delay) {
        // eslint-disable-next-line fp/no-mutation
        animationRef.current = requestAnimationFrame(animate);
        return;
      }

      const progress = Math.min((elapsed - delay) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out
      setAnimatedWidth(easedProgress * textWidth);

      if (progress < 1) {
        // eslint-disable-next-line fp/no-mutation
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setAnimationComplete(true);
      }
    };

    // eslint-disable-next-line fp/no-mutation
    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [delay, duration, textWidth, animationComplete]);

  return (
    <svg className="absolute" height="7" viewBox={`0 0 ${textWidth} 7`} width={textWidth}>
      <defs>
        <mask id={`mask-${textWidth}-${delay}`}>
          <rect fill="white" height="7" width={animationComplete ? textWidth : animatedWidth} x="0" y="0" />
        </mask>
      </defs>
      <path
        d={path}
        fill="none"
        mask={`url(#mask-${textWidth}-${delay})`}
        stroke={colors.textPrimary}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
      />
    </svg>
  );
});

export const LetterRecommendationTaskSuggestion = ({
  baseDuration = 350,
  disabled = false,
  onPress,
  text,
}: LetterRecommendationTaskSuggestionProps) => {
  const [lines, setLines] = useState<Array<{ text: string; width: number }>>([]);
  const [containerWidth, setContainerWidth] = useState(0);
  const [shouldAnimate] = useState(!disabled);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (textRef.current && containerRef.current) {
      const textElement = textRef.current;
      const containerWidth = containerRef.current.offsetWidth;
      setContainerWidth(containerWidth);

      const range = document.createRange();
      range.selectNodeContents(textElement);
      const clientRects = range.getClientRects();

      const newLines = Array.from(clientRects).map((rect) => ({
        text: '',
        width: Math.min(rect.width, containerWidth - 40),
      }));

      setLines(newLines);
    }
  }, [text]);

  const fixedSpeed = useMemo(() => {
    const maxLineWidth = Math.max(...lines.map((line) => line.width));
    return maxLineWidth / baseDuration;
  }, [lines, baseDuration]);

  const lineAnimations = useMemo(
    () =>
      lines.map((line, index) => ({
        delay: shouldAnimate ? index * 160 : 0,
        duration: shouldAnimate ? line.width / fixedSpeed : 0,
      })),
    [lines, shouldAnimate, fixedSpeed],
  );

  return (
    <button
      aria-disabled={disabled}
      aria-label={text}
      className={`paper-suggestion-background relative w-full overflow-hidden rounded-lg ${disabled ? 'paper-suggestion-content-used cursor-default' : 'paper-suggestion-content cursor-pointer'}`}
      disabled={disabled}
      onClick={onPress}
    >
      <YStack
        className="border-borderPrimary p-md text-p-md relative w-full rounded-lg border-2 border-dashed"
        ref={containerRef}
      >
        <XStack className="gap-sm flex items-center justify-between">
          <XStack className="relative flex-1 text-left">
            <Text className="text-textPrimary inline-block" ref={textRef}>
              {text}
            </Text>
            {disabled && lines.length > 0
              ? lines.map((line, index) => (
                  <div
                    // eslint-disable-next-line tailwindcss/no-arbitrary-value
                    className="absolute flex h-[24px] items-center"
                    key={index}
                    style={{ top: `${index * 1.5}rem` }}
                  >
                    <AnimatedStrikeThrough
                      delay={lineAnimations[index]?.delay ?? 0}
                      duration={lineAnimations[index]?.duration ?? 0}
                      fullWidth={containerWidth}
                      textWidth={line.width}
                    />
                  </div>
                ))
              : null}
          </XStack>
          <YStack className="flex items-center justify-center">
            <IconHandwrittenChevronRight height={24} width={24} />
          </YStack>
        </XStack>
      </YStack>
    </button>
  );
};
